import { action, makeObservable, observable, runInAction } from "mobx";
import StockIssueRequestAPI from "../api/StockIssueRequestAPI";
import SwageazyInventoryAPI from "../api/SwageazyInventoryAPI";
import OrderProductsVariantsAPI from "../api/OrderProductsVariantsAPI";
import {
  SampleSirActivityEntry,
  StockIssueRequest,
} from "../../../models/StockIssueRequest/StockIssueRequest";
import { StockIssueRequestFilters } from "../../../models/StockIssueRequest/StockIssueRequestFilters";
import { OrderProductInventoryAvailability } from "../../../models/StockIssueRequest/OrderInventoryAvailability";
import { StockIssueRequestBody } from "../../../models/StockIssueRequest/StockIssueRequestBody";
import { OrderProduct } from "../../../models/Order/OrderProduct";
import {
  buildOrderProductInventoryFromStockDetails,
  buildOrderProductSkuAvailability,
  aggregateProductWarehouseStock,
} from "../helpers/orderInventoryAvailability.helper";

export class StockIssueRequestStore {
  api: StockIssueRequestAPI;
  swageazyInventoryAPI: SwageazyInventoryAPI;
  orderProductsVariantsAPI: OrderProductsVariantsAPI;
  stockIssueRequests: StockIssueRequest[] = [];
  currentRequest: StockIssueRequest | null = null;
  activityLogs: SampleSirActivityEntry[] = [];
  activityLogsLoading: boolean = false;
  total_count: number = 0;
  total_pages: number = 0;
  limit: number = 30;
  orderInventoryAvailabilityByProductId: Record<
    number,
    OrderProductInventoryAvailability
  > = {};
  skuAvailabilityLoadedProductIds: Record<number, boolean> = {};
  isOrderInventoryTotalsLoading: boolean = false;
  loadingSkuAvailabilityProductId: number | null = null;

  constructor() {
    this.api = new StockIssueRequestAPI();
    this.swageazyInventoryAPI = new SwageazyInventoryAPI();
    this.orderProductsVariantsAPI = new OrderProductsVariantsAPI();
    this.stockIssueRequests = [];
    this.currentRequest = null;
    this.activityLogs = [];
    this.activityLogsLoading = false;
    this.total_count = 0;
    this.total_pages = 0;
    this.limit = 30;
    this.orderInventoryAvailabilityByProductId = {};
    this.skuAvailabilityLoadedProductIds = {};
    this.isOrderInventoryTotalsLoading = false;
    this.loadingSkuAvailabilityProductId = null;

    makeObservable(this, {
      stockIssueRequests: observable,
      currentRequest: observable,
      activityLogs: observable,
      activityLogsLoading: observable,
      total_count: observable,
      total_pages: observable,
      limit: observable,
      orderInventoryAvailabilityByProductId: observable,
      skuAvailabilityLoadedProductIds: observable,
      isOrderInventoryTotalsLoading: observable,
      loadingSkuAvailabilityProductId: observable,
      getStockIssueRequests: action,
      getStockIssueRequestById: action,
      getStockIssueRequestActivityLogs: action,
      resetActivityLogs: action,
      loadOrderProductInventoryTotals: action,
      loadOrderProductSkuAvailability: action,
      createStockIssueRequest: action,
      clearOrderInventoryAvailability: action,
    });
  }

  clearOrderInventoryAvailability = () => {
    this.orderInventoryAvailabilityByProductId = {};
    this.skuAvailabilityLoadedProductIds = {};
    this.isOrderInventoryTotalsLoading = false;
    this.loadingSkuAvailabilityProductId = null;
  };

  loadOrderProductInventoryTotals = async (
    products: OrderProduct[],
  ): Promise<void> => {
    if (!products.length) {
      this.clearOrderInventoryAvailability();
      return;
    }

    try {
      runInAction(() => {
        this.isOrderInventoryTotalsLoading = true;
      });

      const stockDetailsList = await Promise.all(
        products.map((product) =>
          this.swageazyInventoryAPI.getProductStockDetails(product.id),
        ),
      );

      runInAction(() => {
        const availabilityByProductId = products.reduce(
          (acc, product, index) => {
            acc[product.id] = buildOrderProductInventoryFromStockDetails({
              product,
              stockDetails: stockDetailsList[index],
            });
            return acc;
          },
          {} as Record<number, OrderProductInventoryAvailability>,
        );

        this.orderInventoryAvailabilityByProductId = availabilityByProductId;
        this.isOrderInventoryTotalsLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.orderInventoryAvailabilityByProductId = {};
        this.isOrderInventoryTotalsLoading = false;
      });
      throw error;
    }
  };

  loadOrderProductSkuAvailability = async (
    orderId: number,
    product: OrderProduct,
  ): Promise<void> => {
    if (!orderId || !product?.id) {
      return;
    }

    if (this.skuAvailabilityLoadedProductIds[product.id]) {
      return;
    }

    try {
      runInAction(() => {
        this.loadingSkuAvailabilityProductId = product.id;
      });

      const [stockDetails, orderVariants] = await Promise.all([
        this.swageazyInventoryAPI.getProductStockDetails(product.id),
        this.orderProductsVariantsAPI.fetchVariants(orderId, product.id),
      ]);

      const skuVariants = buildOrderProductSkuAvailability({
        orderVariants: orderVariants ?? [],
        variantsStock: stockDetails.variants_stock,
      });

      runInAction(() => {
        const current =
          this.orderInventoryAvailabilityByProductId[product.id] ?? {
            product_id: product.id,
            product_name: product.name ?? null,
            ordered_quantity: Number(product.quantity ?? 0),
            total_available_quantity: 0,
            warehouse_stock: [],
            sku_variants: [],
          };

        this.orderInventoryAvailabilityByProductId = {
          ...this.orderInventoryAvailabilityByProductId,
          [product.id]: {
            ...current,
            total_available_quantity:
              aggregateProductWarehouseStock(stockDetails.total_stock).reduce(
                (sum, row) => sum + row.quantity,
                0,
              ) || current.total_available_quantity,
            warehouse_stock: aggregateProductWarehouseStock(
              stockDetails.total_stock,
            ),
            sku_variants: skuVariants,
          },
        };
        this.skuAvailabilityLoadedProductIds = {
          ...this.skuAvailabilityLoadedProductIds,
          [product.id]: true,
        };
        this.loadingSkuAvailabilityProductId = null;
      });
    } catch (error) {
      runInAction(() => {
        this.loadingSkuAvailabilityProductId = null;
      });
      throw error;
    }
  };

  createStockIssueRequest = async (
    body: StockIssueRequestBody,
  ): Promise<StockIssueRequest> => {
    const response = await this.api.createStockIssueRequest(body);
    return response;
  };

  getStockIssueRequestById = async (id: number): Promise<void> => {
    try {
      const response = await this.api.getStockIssueRequestById(id);
      runInAction(() => {
        this.currentRequest = response;
      });
    } catch (error) {
      runInAction(() => {
        this.currentRequest = null;
      });
      console.error("Failed to fetch stock issue request by id:", error);
    }
  };

  getStockIssueRequests = async (
    filters: StockIssueRequestFilters,
  ): Promise<void> => {
    try {
      const response = await this.api.getStockIssueRequests(filters);
      runInAction(() => {
        this.stockIssueRequests = response.list ?? [];
        this.total_count = response.total_count ?? 0;
        this.total_pages = response.total_pages ?? 0;
        this.limit = response.limit ?? 30;
      });
    } catch (error) {
      runInAction(() => {
        this.stockIssueRequests = [];
        this.total_count = 0;
        this.total_pages = 0;
        this.limit = 30;
      });
      console.error("Failed to fetch stock issue requests:", error);
    }
  };

  getStockIssueRequestActivityLogs = async (id: number): Promise<void> => {
    this.activityLogsLoading = true;
    try {
      const response = await this.api.getStockIssueRequestActivityLogs(id);
      runInAction(() => {
        this.activityLogs = response ?? [];
        this.activityLogsLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.activityLogs = [];
        this.activityLogsLoading = false;
      });
      console.error("Failed to fetch stock issue request activity logs:", error);
      throw error;
    }
  };

  resetActivityLogs = () => {
    this.activityLogs = [];
    this.activityLogsLoading = false;
  };
}
